import { Card, CardContent, CardHeader, CardTitle } from '@shared/components/ui/card';
import { Input } from '@shared/components/ui/input';
import { Label } from '@shared/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select';
import { Textarea } from '@shared/components/ui/textarea';
import { useState, useEffect } from 'react';
import { apiRequest } from '@client/lib/queryClient';

interface BusinessInfoStepProps {
  businessType: string;
  shopName: string;
  phone: string;
  email: string;
  website: string;
  address: { street: string; city: string; state: string; zipCode: string };
  shopHours: string;
  defaultShopRate: number;
  taxRate: number;
  currency: string;
  onBusinessTypeChange: (value: string) => void;
  onShopNameChange: (value: string) => void;
  onPhoneChange: (value: string) => void;
  onEmailChange: (value: string) => void;
  onWebsiteChange: (value: string) => void;
  onAddressChange: (value: { street: string; city: string; state: string; zipCode: string }) => void;
  onShopHoursChange: (value: string) => void;
  onDefaultShopRateChange: (value: number) => void;
  onTaxRateChange: (value: number) => void;
  onCurrencyChange: (value: string) => void;
  onValidationChange?: (isValid: boolean) => void;
  onValidationAttempt?: () => void;
  validationAttempted?: boolean;
}

interface BusinessType {
  value: string;
  label: string;
}

const CURRENCIES = [
  { value: 'USD', label: 'US Dollar ($)' },
  { value: 'CAD', label: 'Canadian Dollar (C$)' },
  { value: 'EUR', label: 'Euro (€)' },
  { value: 'GBP', label: 'British Pound (£)' }
];

export default function BusinessInfoStep(props: BusinessInfoStepProps) {
  const [businessTypes, setBusinessTypes] = useState<BusinessType[]>([]);
  const [loading, setLoading] = useState(true);
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});
  const [touchedFields, setTouchedFields] = useState<{[key: string]: boolean}>({});

  // Validation functions
  const validateEmail = (email: string): string | null => {
    if (!email.trim()) return 'Email is required';
    if (!/^([^\s@]+)@([^\s@]+)\.[^\s@]+$/.test(email)) return 'Please enter a valid email address';
    return null;
  };

  const validateZipCode = (zipCode: string): string | null => {
    if (!zipCode.trim()) return 'ZIP code is required';
    if (!/^\d{5}(-\d{4})?$/.test(zipCode)) return 'Please enter a valid ZIP code';
    return null;
  };

  const validateShopName = (shopName: string): string | null => {
    if (!shopName.trim()) return 'Shop name is required';
    if (shopName.length > 100) return 'Shop name must be 100 characters or less';
    return null;
  };

  // Validate all fields
  const validateAllFields = () => {
    // Check if all required fields have values
    const hasBusinessType = !!props.businessType;
    const hasShopName = !!props.shopName && props.shopName.trim().length > 0;
    const hasPhone = !!props.phone && props.phone.trim().length > 0;
    const hasEmail = !!props.email && props.email.trim().length > 0;
    const hasStreet = !!props.address.street && props.address.street.trim().length > 0;
    const hasCity = !!props.address.city && props.address.city.trim().length > 0;
    const hasState = !!props.address.state && props.address.state.trim().length > 0;
    const hasZipCode = !!props.address.zipCode && props.address.zipCode.trim().length > 0;
    
    // Check if default shop rate and tax rate have valid values (they have defaults)
    const hasDefaultShopRate = props.defaultShopRate !== undefined && props.defaultShopRate > 0;
    const hasTaxRate = props.taxRate !== undefined && props.taxRate >= 0;
    
    // Check if email and ZIP code are valid (if they have values)
    const emailValid = !props.email || validateEmail(props.email) === null;
    const zipCodeValid = !props.address.zipCode || validateZipCode(props.address.zipCode) === null;
    const shopNameValid = !props.shopName || validateShopName(props.shopName) === null;
    
    return hasBusinessType && hasShopName && hasPhone && hasEmail && hasStreet && 
           hasCity && hasState && hasZipCode && hasDefaultShopRate && hasTaxRate && 
           emailValid && zipCodeValid && shopNameValid;
  };

  // Function to mark all required fields as touched
  const markAllFieldsAsTouched = () => {
    setTouchedFields({
      businessType: true,
      shopName: true,
      phone: true,
      email: true,
      street: true,
      city: true,
      state: true,
      zipCode: true
    });
  };

  // Handle validation attempt from parent
  useEffect(() => {
    if (props.validationAttempted) {
      markAllFieldsAsTouched();
      // Run validation on all fields
      const errors: {[key: string]: string} = {};
      
      if (!props.businessType) errors.businessType = 'Business type is required';
      if (!props.shopName || props.shopName.trim().length === 0) errors.shopName = 'Shop name is required';
      if (!props.phone || props.phone.trim().length === 0) errors.phone = 'Phone number is required';
      if (!props.email || props.email.trim().length === 0) errors.email = 'Email is required';
      if (!props.address.street || props.address.street.trim().length === 0) errors.street = 'Street address is required';
      if (!props.address.city || props.address.city.trim().length === 0) errors.city = 'City is required';
      if (!props.address.state || props.address.state.trim().length === 0) errors.state = 'State is required';
      if (!props.address.zipCode || props.address.zipCode.trim().length === 0) errors.zipCode = 'ZIP code is required';
      
      // Format validation
      if (props.email && props.email.trim().length > 0) {
        const emailError = validateEmail(props.email);
        if (emailError) errors.email = emailError;
      }
      
      if (props.address.zipCode && props.address.zipCode.trim().length > 0) {
        const zipCodeError = validateZipCode(props.address.zipCode);
        if (zipCodeError) errors.zipCode = zipCodeError;
      }
      
      if (props.shopName && props.shopName.trim().length > 0) {
        const shopNameError = validateShopName(props.shopName);
        if (shopNameError) errors.shopName = shopNameError;
      }
      
      setValidationErrors(errors);
    }
  }, [props.validationAttempted]);

  // Notify parent when validation changes - when field values change
  useEffect(() => {
    const isValid = validateAllFields();
    props.onValidationChange?.(isValid);
  }, [props.businessType, props.shopName, props.phone, props.email, props.address.street, props.address.city, props.address.state, props.address.zipCode, props.defaultShopRate, props.taxRate]);

  useEffect(() => {
    const fetchBusinessTypes = async () => {
      try {
        const response = await apiRequest('GET', '/api/v1/onboarding/business-types');
        console.log('[BusinessInfoStep] Business types API response:', response);
        const data = await response.json();
        console.log('[BusinessInfoStep] Parsed business types data:', data);
        // Extract businessTypes array from the response object
        const businessTypesArray = data.businessTypes || data;
        setBusinessTypes(Array.isArray(businessTypesArray) ? businessTypesArray : []);
      } catch (error) {
        console.error('[BusinessInfoStep] Failed to fetch business types:', error);
        // No fallback - rely exclusively on database
        setBusinessTypes([]);
      } finally {
        setLoading(false);
      }
    };

    fetchBusinessTypes();
  }, []);

  console.log('[BusinessInfoStep] Rendering with props:', {
    businessType: props.businessType,
    shopName: props.shopName,
    phone: props.phone,
    email: props.email,
    address: props.address,
    defaultShopRate: props.defaultShopRate,
    taxRate: props.taxRate
  });

  const handleAddressChange = (field: string, value: string) => {
    console.log('[BusinessInfoStep] handleAddressChange:', { field, value });
    props.onAddressChange({
      ...props.address,
      [field]: value
    });
  };

  return (
    <div className="space-y-6">
      {/* Business Information */}
      <Card>
        <CardHeader>
          <CardTitle>Business Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="businessType">Business Type *</Label>
              <Select 
                value={props.businessType} 
                onValueChange={(value) => {
                  props.onBusinessTypeChange(value);
                  setTouchedFields(prev => ({ ...prev, businessType: true }));
                }}
              >
                <SelectTrigger className={touchedFields.businessType && !props.businessType ? 'border-red-500' : ''}>
                  <SelectValue placeholder="Select business type" />
                </SelectTrigger>
                <SelectContent>
                  {loading ? (
                    <SelectItem value="loading" disabled>Loading business types...</SelectItem>
                  ) : businessTypes.length === 0 ? (
                    <SelectItem value="no-data" disabled>No business types available</SelectItem>
                  ) : (
                    businessTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              {touchedFields.businessType && !props.businessType && (
                <p className="text-sm text-red-500">Business type is required</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="shopName">Shop Name *</Label>
              <Input
                id="shopName"
                value={props.shopName}
                onChange={(e) => {
                  props.onShopNameChange(e.target.value);
                  // Clear validation error when user starts typing
                  if (validationErrors.shopName) {
                    setValidationErrors(prev => ({ ...prev, shopName: '' }));
                  }
                }}
                onBlur={() => {
                  setTouchedFields(prev => ({ ...prev, shopName: true }));
                  // Validate shop name when user leaves the field
                  const shopNameError = validateShopName(props.shopName);
                  if (shopNameError) {
                    setValidationErrors(prev => ({ ...prev, shopName: shopNameError }));
                  }
                }}
                placeholder="Enter shop name"
                className={touchedFields.shopName && validationErrors.shopName ? 'border-red-500' : ''}
              />
              {touchedFields.shopName && validationErrors.shopName && (
                <p className="text-sm text-red-500">{validationErrors.shopName}</p>
              )}
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number *</Label>
              <Input
                id="phone"
                type="tel"
                value={props.phone}
                onChange={(e) => {
                  props.onPhoneChange(e.target.value);
                  setTouchedFields(prev => ({ ...prev, phone: true }));
                }}
                onBlur={() => setTouchedFields(prev => ({ ...prev, phone: true }))}
                placeholder="(555) 123-4567"
                className={touchedFields.phone && !props.phone ? 'border-red-500' : ''}
              />
              {touchedFields.phone && !props.phone && (
                <p className="text-sm text-red-500">Phone number is required</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                type="email"
                value={props.email}
                onChange={(e) => {
                  props.onEmailChange(e.target.value);
                  // Clear validation error when user starts typing
                  if (validationErrors.email) {
                    setValidationErrors(prev => ({ ...prev, email: '' }));
                  }
                }}
                onBlur={() => {
                  setTouchedFields(prev => ({ ...prev, email: true }));
                  // Validate email when user leaves the field
                  const emailError = validateEmail(props.email);
                  if (emailError) {
                    setValidationErrors(prev => ({ ...prev, email: emailError }));
                  }
                }}
                placeholder="shop@example.com"
                className={touchedFields.email && validationErrors.email ? 'border-red-500' : ''}
              />
              {touchedFields.email && validationErrors.email && (
                <p className="text-sm text-red-500">{validationErrors.email}</p>
              )}
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
            <div className="space-y-2">
              <Label htmlFor="website">Website (Optional)</Label>
              <Input
                id="website"
                type="url"
                value={props.website}
                onChange={(e) => props.onWebsiteChange(e.target.value)}
                placeholder="https://example.com"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Address */}
      <Card>
        <CardHeader>
          <CardTitle>Business Address</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="street">Street Address *</Label>
            <Input
              id="street"
              value={props.address.street}
              onChange={(e) => {
                handleAddressChange('street', e.target.value);
                setTouchedFields(prev => ({ ...prev, street: true }));
              }}
              onBlur={() => setTouchedFields(prev => ({ ...prev, street: true }))}
              placeholder="123 Main Street"
              className={touchedFields.street && !props.address.street ? 'border-red-500' : ''}
            />
            {touchedFields.street && !props.address.street && (
              <p className="text-sm text-red-500">Street address is required</p>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="city">City *</Label>
              <Input
                id="city"
                value={props.address.city}
                onChange={(e) => {
                  handleAddressChange('city', e.target.value);
                  setTouchedFields(prev => ({ ...prev, city: true }));
                }}
                onBlur={() => setTouchedFields(prev => ({ ...prev, city: true }))}
                placeholder="City"
                className={touchedFields.city && !props.address.city ? 'border-red-500' : ''}
              />
              {touchedFields.city && !props.address.city && (
                <p className="text-sm text-red-500">City is required</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="state">State *</Label>
              <Input
                id="state"
                value={props.address.state}
                onChange={(e) => {
                  handleAddressChange('state', e.target.value);
                  setTouchedFields(prev => ({ ...prev, state: true }));
                }}
                onBlur={() => setTouchedFields(prev => ({ ...prev, state: true }))}
                placeholder="State"
                className={touchedFields.state && !props.address.state ? 'border-red-500' : ''}
              />
              {touchedFields.state && !props.address.state && (
                <p className="text-sm text-red-500">State is required</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="zipCode">ZIP Code *</Label>
              <Input
                id="zipCode"
                value={props.address.zipCode}
                onChange={(e) => {
                  handleAddressChange('zipCode', e.target.value);
                  // Clear validation error when user starts typing
                  if (validationErrors.zipCode) {
                    setValidationErrors(prev => ({ ...prev, zipCode: '' }));
                  }
                }}
                onBlur={() => {
                  setTouchedFields(prev => ({ ...prev, zipCode: true }));
                  // Validate ZIP code when user leaves the field
                  const zipCodeError = validateZipCode(props.address.zipCode);
                  if (zipCodeError) {
                    setValidationErrors(prev => ({ ...prev, zipCode: zipCodeError }));
                  }
                }}
                placeholder="12345"
                className={touchedFields.zipCode && validationErrors.zipCode ? 'border-red-500' : ''}
              />
              {touchedFields.zipCode && validationErrors.zipCode && (
                <p className="text-sm text-red-500">{validationErrors.zipCode}</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Shop Hours */}
      <Card>
        <CardHeader>
          <CardTitle>Shop Hours</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label htmlFor="shopHours">Operating Hours</Label>
            <Textarea
              id="shopHours"
              value={props.shopHours}
              onChange={(e) => props.onShopHoursChange(e.target.value)}
              placeholder="Monday - Friday: 8:00 AM - 6:00 PM&#10;Saturday: 9:00 AM - 4:00 PM&#10;Sunday: Closed"
              rows={4}
            />
          </div>
        </CardContent>
      </Card>

      {/* Pricing & Currency */}
      <Card>
        <CardHeader>
          <CardTitle>Default Pricing & Currency</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="defaultShopRate">Default Shop Rate ($/hour) *</Label>
              <Input
                id="defaultShopRate"
                type="number"
                min="0"
                step="0.01"
                value={props.defaultShopRate}
                onChange={(e) => props.onDefaultShopRateChange(Number(e.target.value))}
                className={props.defaultShopRate <= 0 ? 'border-red-500' : ''}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="taxRate">Tax Rate (%) *</Label>
              <Input
                id="taxRate"
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={props.taxRate}
                onChange={(e) => props.onTaxRateChange(Number(e.target.value))}
                className={props.taxRate < 0 ? 'border-red-500' : ''}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="currency">Currency</Label>
              <Select value={props.currency} onValueChange={props.onCurrencyChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select currency" />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((currency) => (
                    <SelectItem key={currency.value} value={currency.value}>
                      {currency.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}